import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { getCurrentLocationEntity } from 'selectors/session';

import CheckboxField from 'components/forms/CheckboxField';
import TooltipWrapper from 'components/TooltipWrapper';

import { toI18n } from 'util/i18n';
import { toCurrency } from 'util/toCurrency';

const TippableCheckbox = ({
  wage_rate,
  location,
  onChange,
  value,
  ...props
}) => {
  const [minWageRate, setMinWageRate] = useState(
    location.get('state_min_wage')
  );
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(false);

  useEffect(() => {
    setIsCheckboxDisabled(!wage_rate || Number(wage_rate) >= minWageRate);
  }, [setIsCheckboxDisabled, wage_rate, minWageRate]);

  useEffect(() => {
    setMinWageRate(location.get('state_min_wage'));
  }, [location]);

  useEffect(
    () => {
      if (onChange && isCheckboxDisabled) {
        onChange(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCheckboxDisabled]
  );

  const validEffectiveDateForJobSettings = useMemo(
    () => location?.get('validEffectiveDateForJobSettings'),
    [location]
  );

  const handleTippableI18nText = useCallback(
    () =>
      toI18n(
        isCheckboxDisabled
          ? 'team.employee_profile.access_wages.disabled_wage_rate_tooltip'
          : 'team.employee_profile.access_wages.enabled_wage_rate_tooltip',
        {
          props: {
            min_wage_rate: toCurrency(minWageRate, { precision: 2 }),
            effective_date: validEffectiveDateForJobSettings,
          },
        }
      ),
    [minWageRate, isCheckboxDisabled, validEffectiveDateForJobSettings]
  );

  const handleTooltipRender = useCallback(() => {
    const tooltipObject = {
      size: 14,
      color: 'gray-light',
      theme: 'navy',
      iconType: 'questionCircleLiteProIcon',
      dataTestId: 'tippable-checkbox-tooltip',
    };
    return (
      <CheckboxField
        {...props}
        value={value}
        onChange={onChange}
        data-testid="wage_rate_checkbox"
        disabled={isCheckboxDisabled}
        fontSize="fs16"
        titleI18n="team.employee_profile.access_wages.tipped_checkbox"
        tooltip={
          isCheckboxDisabled
            ? {
                ...tooltipObject,
              }
            : {
                ...tooltipObject,
                preferPlace: 'below',
                content: handleTippableI18nText(),
              }
        }
        color="primary"
      />
    );
  }, [isCheckboxDisabled, handleTippableI18nText, onChange, props, value]);

  if (isCheckboxDisabled) {
    return (
      <TooltipWrapper
        theme="navy"
        preferPlace="below"
        content={handleTippableI18nText()}
      >
        {handleTooltipRender()}
      </TooltipWrapper>
    );
  }

  return handleTooltipRender();
};

TippableCheckbox.propTypes = {
  wage_rate: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default connect(
  (state, props) => ({
    location: getCurrentLocationEntity(state, props),
  }),
  {}
)(TippableCheckbox);
